import React, { useState, useEffect, useCallback } from 'react';

import { Box, Button, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Close } from '@mui/icons-material';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';

import BackButton from '../../components/common/BackButton';
import ModalityMultiSelect from '../../components/common/ModalityMultiSelect';
import TimePeriodSelector from '../../components/common/TimePeriodSelector';
import TypeMultiSelect from '../../components/common/TypeMultiSelect';
import FacilityComparisonChart from '../../components/facility/FacilityComparisonChart';
import ObservationChart from '../../components/observation/ObservationChart';
import CategoryParetoChart from '../../components/technologist/CategoryParetoChart';
import TechnologistCategoryChart from '../../components/technologist/TechnologistCategoryChart';
import TechnologistComparisonChart from '../../components/technologist/TechnologistComparisonChart';
import { GRANULARITY_VALUES } from '../../constants/observations';
import FacilityService from '../../services/facility.service';
import ObservationsReportService from '../../services/observations-report.service';
import TechnologistService from '../../services/technologist.service';

const facilityService = new FacilityService();
const observationsReportService = new ObservationsReportService();
const technologistService = new TechnologistService();

const useStyles = makeStyles((theme) => ({
  chartContainer: {
    margin: '20px 0',
    padding: 20,
  },
  closeIcon: {
    '&:hover': {
      color: '#333',
      cursor: 'pointer',
    },
    color: '#aaa',
    display: 'none !important',
    padding: 2,
    position: 'absolute',
    right: 12,
    top: 4,
  },
  filterContainer: {
    margin: '20px 0',
    padding: 20,
  },
  inputContainer: {
    '& .NativeDatepicker, .NativeDatepicker .MuiBox-root, & select': {
      height: 30,
      width: 140,
    },
    '&:hover>.MuiSvgIcon-fontSizeSmall': {
      display: 'block !important',
    },
    position: 'relative',
  },
  modalitySelect: {
    width: '180px !important',
  },
  pageContent: {
    marginBottom: 60,
    width: '100%',
  },
  pageHeader: {
    fontWeight: 700,
  },
  refreshButton: {
    height: 30,
    marginLeft: '10px',
    minWidth: 'auto',
  },
  root: {
    '& .recharts-cartesian-axis-tick-line': {
      stroke: 'transparent',
    },
    '& .recharts-cartesian-grid-horizontal line': {
      stroke: '#eaeaea',
    },
    flexGrow: 1,
  },
  searchInputRoot: {
    '& .MuiInput-input': {
      height: 30,
      padding: '0 5px',
    },
    '& .MuiInput-root': {
      height: 30,
    },
    height: 30,
  },
  select: {
    '&:focus': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      outline: 'none',
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      cursor: 'pointer',
    },
    border: '1px solid #C8C8C8',
    borderRadius: 8,
    color: '#999',
    height: 40,
    padding: '0 16px',
    width: 166,
  },
}));

export default function ObservationReports() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const getInitialState = () => {
    const params = new URLSearchParams(location.search);

    return {
      department: params.get('department') || '',
      endDate: params.get('endDate') || '',
      facility: params.get('facility') || '',
      modality: params.get('modality') ? params.get('modality').split(',') : [],
      startDate: params.get('startDate') || '',
      timeGranularity: params.get('timeGranularity') || GRANULARITY_VALUES.MONTHLY,
      type: params.get('type') ? params.get('type').split(',') : [],
    };
  };

  const initialState = getInitialState();

  const [selectedType, setSelectedType] = useState(initialState.type);
  const [selectedModality, setSelectedModality] = useState(initialState.modality);
  const [selectedFacility, setSelectedFacility] = useState(initialState.facility || '');
  const [selectedDepartment, setSelectedDepartment] = useState(initialState.department || '');
  const [startDate, setStartDate] = useState(initialState.startDate);
  const [endDate, setEndDate] = useState(initialState.endDate);
  const [timeGranularity, setTimeGranularity] = useState(initialState.timeGranularity);

  const [ObservationSummaryData, setObservationSummaryData] = useState(null);
  const [loadingObservationSummary, setLoadingObservationSummary] = useState(false);
  const [ObservationSummaryError, setObservationSummaryError] = useState(false);

  const [categoryData, setCategoryData] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState(null);

  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);

  const [facilityComparisonData, setFacilityComparisonData] = useState(null);
  const [facilityComparisonLoading, setFacilityComparisonLoading] = useState(false);
  const [facilityComparisonError, setFacilityComparisonError] = useState(null);

  const [facilities, setFacilities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const buildQueryParams = useCallback(() => {
    const queryParams = {};

    if (selectedType && selectedType.length > 0) queryParams.type = selectedType.join(',');
    if (selectedModality && selectedModality.length > 0) queryParams.modality = selectedModality.join(',');
    if (selectedFacility) queryParams.facilityId = selectedFacility;
    if (selectedDepartment) queryParams.departmentId = selectedDepartment;

    if (startDate) {
      queryParams.startDate = startDate;
    }

    if (endDate) {
      queryParams.endDate = endDate;
    }

    if (timeGranularity) queryParams.timeGranularity = timeGranularity;

    return queryParams;
  }, [selectedType, selectedModality, selectedFacility, selectedDepartment, startDate, endDate, timeGranularity]);

  const updateUrlParams = useCallback(() => {
    const params = new URLSearchParams();

    if (selectedType && selectedType.length > 0) params.set('type', selectedType.join(','));
    if (selectedModality && selectedModality.length > 0) params.set('modality', selectedModality.join(','));
    if (selectedFacility) params.set('facility', selectedFacility);
    if (selectedDepartment) params.set('department', selectedDepartment);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (timeGranularity) params.set('timeGranularity', timeGranularity);

    history.replace({ search: params.toString() });
  }, [history, selectedType, selectedModality, selectedFacility, selectedDepartment, startDate, endDate, timeGranularity]);

  const fetchSummaryData = useCallback(async () => {
    setLoadingObservationSummary(true);
    setObservationSummaryError(false);
    try {
      const queryParams = buildQueryParams();

      const response = await observationsReportService.getObservationSummary(queryParams);

      const apiData = response?.data || [];

      const start = moment(startDate, 'MM-DD-YYYY');
      const end = moment(endDate, 'MM-DD-YYYY');
      const allExpectedDates = [];

      const current = start.clone();
      const format = 'MM/DD/YYYY';

      while (current.isSameOrBefore(end)) {
        let dateString;

        switch (timeGranularity) {
          case GRANULARITY_VALUES.DAILY:
            dateString = current.format(format);
            current.add(1, 'day');
            break;
          case GRANULARITY_VALUES.MONTHLY: {
            const month = current.month() + 1;
            dateString = `${month < 10 ? `0${month}` : month}/${current.year()}`;
            current.add(1, 'month');
            break;
          }
          case GRANULARITY_VALUES.YEARLY:
            dateString = `${current.format('YYYY')}`;
            current.add(1, 'year');
            break;
          default:
            dateString = current.format(format);
            current.add(1, 'month');
        }

        allExpectedDates.push({
          callback: 0,
          date: dateString,
          routine: 0,
          stat: 0,
        });
      }

      const formattedData = allExpectedDates.map((expectedDate) => {
        let matchingItem = apiData.find((item) => item.date === expectedDate.date);

        if (!matchingItem) {
          matchingItem = apiData.find((item) => {
            if (!item.date) return false;

            if (timeGranularity === GRANULARITY_VALUES.MONTHLY) {
              const normalizeDate = (dateStr) => {
                const parts = dateStr.split('/');
                if (parts.length !== 2) return dateStr;
                const month = parseInt(parts[0], 10);
                return `${month < 10 ? `0${month}` : month}/${parts[1]}`;
              };

              const normalizedExpected = normalizeDate(expectedDate.date);
              const normalizedItem = normalizeDate(item.date);

              return normalizedExpected === normalizedItem;
            }
            return false;
          });
        }

        if (matchingItem) {
          return {
            callback: matchingItem.callback || 0,
            date: expectedDate.date,
            routine: matchingItem.routine || 0,
            stat: matchingItem.stat || 0,
          };
        }

        return expectedDate;
      });

      formattedData.sort((a, b) => {
        let formatString;

        switch (timeGranularity) {
          case GRANULARITY_VALUES.DAILY:
            formatString = 'MM/DD/YYYY';
            break;
          case GRANULARITY_VALUES.MONTHLY:
            formatString = 'MM/YYYY';
            break;
          case GRANULARITY_VALUES.YEARLY:
            formatString = 'YYYY';
            break;
          default:
            formatString = 'MM/DD/YYYY';
        }

        return moment(b.date, formatString).valueOf() - moment(a.date, formatString).valueOf();
      });

      setObservationSummaryData(formattedData);
    } catch (error) {
      console.error('Error fetching monthly summary data:', error);
      setObservationSummaryError(error.message || 'Failed to load data');
    } finally {
      setLoadingObservationSummary(false);
    }
  }, [buildQueryParams, startDate, endDate]);

  const fetchFacilities = useCallback(async () => {
    try {
      setLoadingFacilities(true);
      const response = await facilityService.getAllFacilities();
      if (response) {
        setFacilities(response);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
    } finally {
      setLoadingFacilities(false);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      setLoadingDepartments(true);
      let response;

      if (selectedFacility) {
        response = await facilityService.getDepartments(selectedFacility);
      } else {
        response = await facilityService.getAllDepartments();
      }

      if (response) {
        setDepartments(response);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setLoadingDepartments(false);
    }
  }, [selectedFacility]);

  const fetchCategoryData = useCallback(async () => {
    setCategoryLoading(true);
    setCategoryError(null);

    try {
      const filters = {
        departmentId: selectedDepartment,
        facilityId: selectedFacility,
        modality: selectedModality,
        type: selectedType,
      };

      const response = await technologistService.getCategoryStatistics(null, startDate, endDate, filters);
      setCategoryData(response);
    } catch (err) {
      console.error('Error fetching category statistics:', err);
      setCategoryError(err.message || 'Failed to load category statistics');
    } finally {
      setCategoryLoading(false);
    }
  }, [startDate, endDate, selectedType, selectedModality, selectedFacility, selectedDepartment]);

  const fetchComparisonData = useCallback(async () => {
    setComparisonLoading(true);
    setComparisonError(null);

    try {
      const queryParams = buildQueryParams();
      const response = await observationsReportService.getTechnologistComparison(queryParams);
      setComparisonData(response);
    } catch (err) {
      console.error('Error fetching technologist comparison:', err);
      setComparisonError(err.message || 'Failed to load technologist comparison');
    } finally {
      setComparisonLoading(false);
    }
  }, [buildQueryParams]);

  const fetchFacilityComparisonData = useCallback(async () => {
    setFacilityComparisonLoading(true);
    setFacilityComparisonError(null);

    try {
      const queryParams = buildQueryParams();
      const response = await observationsReportService.getFacilityComparison(queryParams);
      setFacilityComparisonData(response);
    } catch (err) {
      console.error('Error fetching facility comparison:', err);
      setFacilityComparisonError(err.message || 'Failed to load facility comparison');
    } finally {
      setFacilityComparisonLoading(false);
    }
  }, [buildQueryParams]);

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  useEffect(() => {
    updateUrlParams();
    fetchSummaryData();
    fetchCategoryData();
    fetchComparisonData();
    fetchFacilityComparisonData();
  }, [selectedType, selectedModality, selectedFacility, selectedDepartment, startDate, endDate, timeGranularity]);

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const handleTypeChange = (value) => {
    setSelectedType(value);
  };

  const handleModalityChange = (value) => {
    setSelectedModality(value);
  };

  const handleFacilityChange = (event) => {
    const facilityId = event.target.value;

    setSelectedFacility(facilityId);
    setSelectedDepartment('');
  };

  const resetFacilityFilter = () => {
    setSelectedFacility('');
    setSelectedDepartment('');
    setSelectedDepartment('');
  };

  const handleDepartmentChange = (event) => {
    setSelectedDepartment(event.target.value);
  };

  const resetDepartmentFilter = () => {
    setSelectedDepartment('');
  };
  const handleRefreshData = () => {
    fetchSummaryData();
    fetchCategoryData();
    fetchComparisonData();
    fetchFacilityComparisonData();
  };

  const handleTimeGranularityChange = (value) => {
    setTimeGranularity(value);
  };

  const handleTimePeriodChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleTechnologistClick = (technologistId) => {
    window.scrollTo(0, 0);
    history.push(`/technologist-summary/${technologistId}`);
  };

  return (
    <div className={classes.root}>
      <Grid container style={{ padding: '40px 4rem' }} className={classes.pageContent}>
        <Grid item xs={12} style={{ alignItems: 'start', display: 'flex', flexDirection: 'column', justifyContent: 'left', marginBottom: '20px', paddingLeft: '8px' }}>
          <BackButton route="/worklist" goBackHistory />

          <Typography variant="h4" align="left" className={classes.pageHeader}>
            Reports
          </Typography>
        </Grid>

        <Box width="100%" display="flex" flexDirection="column" style={{ gap: '14px' }}>
          <TimePeriodSelector onUpdate={handleTimePeriodChange} timeGranularity={timeGranularity} onGranularityChange={handleTimeGranularityChange} />

          <Box alignItems="center" width="100%" display="flex" flexWrap="wrap" style={{ gap: '10px' }}>
            <ModalityMultiSelect selectedModality={selectedModality} onChange={handleModalityChange} className={classes.inputContainer} multiple />

            <TypeMultiSelect selectedType={selectedType} onChange={handleTypeChange} className={classes.inputContainer} multiple />

            <div className={classes.inputContainer}>
              <select aria-label="Facility" disabled={loadingFacilities} name="facility" className={classes.select} value={selectedFacility} onChange={handleFacilityChange}>
                <option value="">All Facilities</option>
                {facilities.map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name}
                  </option>
                ))}
              </select>

              {selectedFacility && <Close className={classes.closeIcon} fontSize="small" onClick={resetFacilityFilter} />}
            </div>

            {selectedFacility && (
              <div className={classes.inputContainer}>
                <select
                  aria-label="Department"
                  disabled={loadingDepartments}
                  name="department"
                  className={classes.select}
                  value={selectedDepartment}
                  onChange={handleDepartmentChange}>
                  <option value="">All Departments</option>
                  {departments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>
                {selectedDepartment && <Close className={classes.closeIcon} fontSize="small" onClick={resetDepartmentFilter} />}
              </div>
            )}

            <Button color="primary" variant="contained" onClick={handleRefreshData} className={classes.refreshButton} disabled={loadingObservationSummary}>
              Update Graph
            </Button>
          </Box>

          {/* Quality Observations Chart */}
          <Grid item xs={12} className={classes.chartContainer}>
            <ObservationChart data={ObservationSummaryData} loading={loadingObservationSummary} error={ObservationSummaryError} onRefresh={fetchSummaryData} />
          </Grid>

          <Box display="flex" flexDirection="row" gridGap="16px">
            {/* Category Statistics Chart */}
            <Grid item xs={5} className={classes.chartContainer}>
              <TechnologistCategoryChart data={categoryData} loading={categoryLoading} error={categoryError} startDate={startDate} endDate={endDate} />
            </Grid>

            {/* Category Pareto Chart */}
            <Grid item xs={7} className={classes.chartContainer}>
              <CategoryParetoChart data={categoryData} loading={categoryLoading} error={categoryError} startDate={startDate} endDate={endDate} />
            </Grid>
          </Box>

          {/* Facility Comparison Chart - 50% width */}
          <Grid item xs={6} style={{ display: 'flex', justifyContent: 'center' }}>
            <Box style={{ width: '100%' }}>
              <FacilityComparisonChart data={facilityComparisonData} loading={facilityComparisonLoading} error={facilityComparisonError} startDate={startDate} endDate={endDate} />
            </Box>
          </Grid>

          {/* Technologist Comparison Chart */}
          <Grid item xs={12} className={classes.chartContainer}>
            <TechnologistComparisonChart
              data={comparisonData}
              loading={comparisonLoading}
              error={comparisonError}
              startDate={startDate}
              endDate={endDate}
              onTechnologistClick={handleTechnologistClick}
            />
          </Grid>
        </Box>
      </Grid>
    </div>
  );
}
